import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserManagementDto } from '../models/user-management.dto';
import { UpdateClinicUserRequest } from '../models/update-clinic-user-request.model';
import { ClinicMembership } from '../models/clinic-membership.model';
import { PendingClinicInvitationResponse } from '../models/pending-clinic-invitation-response.model';
import { EmployeeClinicMembershipDto } from '../models/employee-clinic-membership.dto';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private baseUrl = environment.APP_BASE_URL + '/clinic-portal/user-management';

  constructor(private http: HttpClient) {}

  getClinicUsers(search?: string, role?: string, status?: string): Observable<UserManagementDto[]> {
    const params: Record<string, string> = {};
    if (search) params['search'] = search;
    if (role) params['role'] = role;
    if (status) params['status'] = status;
    return this.http.get<UserManagementDto[]>(this.baseUrl, { params });
  }

  updateClinicUser(
    userId: string,
    updateRequest: UpdateClinicUserRequest,
  ): Observable<UserManagementDto> {
    return this.http.put<UserManagementDto>(`${this.baseUrl}/${userId}`, updateRequest);
  }

  resetPassword(userId: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/${userId}/reset-password`, {});
  }

  toggleUserStatus(userId: string): Observable<UserManagementDto> {
    return this.http.patch<UserManagementDto>(`${this.baseUrl}/${userId}/toggle-status`, {});
  }

  /** Toggle a doctor's clinic_membership status (does NOT touch user.isActive). */
  toggleDoctorMembershipStatus(clinicMembershipId: string): Observable<UserManagementDto> {
    return this.http.patch<UserManagementDto>(
      `${this.baseUrl}/memberships/${clinicMembershipId}/toggle-status`,
      {},
    );
  }

  /** Invite a doctor to the clinic by license number (manager only). */
  addDoctorToClinic(licenseNumber: string): Observable<ClinicMembership> {
    return this.http.post<ClinicMembership>(`${this.baseUrl}/add-doctor`, { licenseNumber });
  }

  /** Returns all active clinic memberships for the authenticated doctor (for switch clinic). */
  getClinicMemberships(): Observable<ClinicMembership[]> {
    return this.http.get<ClinicMembership[]>(`${this.baseUrl}/clinic-memberships`);
  }

  /** Returns pending clinic invitations for the authenticated doctor. */
  getPendingClinicInvitations(): Observable<PendingClinicInvitationResponse[]> {
    return this.http.get<PendingClinicInvitationResponse[]>(`${this.baseUrl}/pending-invitations`);
  }

  /** Accept or reject a clinic invitation. */
  respondToInvitation(membershipId: string, accept: boolean): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/pending-invitations/${membershipId}/respond`,
      null,
      { params: { accept: String(accept) } },
    );
  }

  // ── Doctor-access link management ──────────────────────────────────────────

  /** Returns all active clinic memberships (doctors) annotated with linked=true/false for this employee. */
  getEmployeeMemberships(userId: string): Observable<EmployeeClinicMembershipDto[]> {
    return this.http.get<EmployeeClinicMembershipDto[]>(`${this.baseUrl}/${userId}/memberships`);
  }

  /** Links the employee to a specific clinic_membership (grants access to that doctor). */
  linkEmployeeToMembership(userId: string, clinicMembershipId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/${userId}/memberships/${clinicMembershipId}`,
      null,
    );
  }

  /** Removes the link between an employee and a specific clinic_membership. */
  unlinkEmployeeFromMembership(userId: string, clinicMembershipId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${userId}/memberships/${clinicMembershipId}`);
  }
}
